import ApexCharts from 'apexcharts';

document.addEventListener('DOMContentLoaded', function () {
    if (typeof wpDataChartsCallbacks === 'undefined') {
        window.wpDataChartsCallbacks = {};
    }

    const chartTypes = [
        { selector: '.js-fund-allocation-charts [id^="wpDataChart_"]', callback: fundAllocationCharts },
        { selector: '.js-growth-chart [id^="wpDataChart_"]', callback: growthChart },
        { selector: '.js-performance-benchmarks-chart [id^="wpDataChart_"]', callback: performanceBenchmarkChart },
    ];

    chartTypes.forEach((type) => {
        document.querySelectorAll(type.selector).forEach((chart) => {
            const chartId = chart.id.split('_')[1]; // Extract the chart ID
            if (chartId) {
                wpDataChartsCallbacks[chartId] = type.callback;
            }
        });
    });

    // Function to set CSS variables for chart legends
    function setLegendHeights() {
        const charts = ['#chart-1', '#chart-2'];
        let cssVariables = '';

        charts.forEach((chartSelector, index) => {
            const legend = document.querySelector(`${chartSelector} .apexcharts-legend`);
            const height = legend ? window.getComputedStyle(legend).height : '0px';
            cssVariables += `--chart-${index + 1}-legend-height: ${height};\n`;
        });

        // Update or create the style element
        let styleElement = document.getElementById('chart-legend-heights-css');
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = 'chart-legend-heights-css';
            document.head.appendChild(styleElement);
        }
        styleElement.textContent = `:root {\n${cssVariables}}`;
    }

    // Debounce function to limit function calls during resize events
    function debounce(func, wait = 100) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    // Event handler for window resize
    const handleResize = debounce(() => {
        setLegendHeights();
    });

    // Initialize on DOMContentLoaded
    setLegendHeights();

    // Add event listener for window resize
    window.addEventListener('resize', handleResize);

    // Chart customization functions
    function fundAllocationCharts(obj) {
        obj.options = {
            ...obj.options,
            chart: {
                type: 'donut',
                parentHeightOffset: 0,
                sparkline: { enabled: true },
                width: '100%',
                events: {
                    mounted: setLegendHeights,
                },
            },
            colors: [
                '#f77214',
                '#404653',
                '#1e81a2',
                '#bf2630',
                '#5e3578',
                '#39b259',
                '#35c4e9',
                '#000',
                '#FFE3A9',
                '#FFC3C3',
                '#FF5D5D',
            ],
            plotOptions: {
                pie: { donut: { size: '35%' } },
            },
            legend: {
                show: true,
                floating: true,
                position: 'right',
                formatter: (seriesName, opts) => {
                    const seriesValue = opts.w.globals.series[opts.seriesIndex];
                    return `<span class="legend-series-name">${seriesName}</span><span class="legend-data-label">${(seriesValue || 0).toFixed(2)}%</span>`;
                },
            },
            responsive: [
                {
                    breakpoint: 1280,
                    options: {
                        legend: {
                            position: 'bottom',
                            horizontalAlign: 'center',
                        },
                    },
                },
            ],
        };
    }

    function growthChart(obj) {
        obj.options = {
            ...obj.options,
            chart: {
                type: 'line',
                height: '640px',
                width: '100%',
                parentHeightOffset: 0,
                zoom: { enabled: false },
                toolbar: { show: false },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800,
                    animateGradually: { enabled: true, delay: 150 },
                    dynamicAnimation: { enabled: true, speed: 350 },
                },
                events: {
                    mounted: (chart) => {
                        setTimeout(() => chart.windowResizeHandler(), 500);
                    },
                },
            },
            grid: {
                show: true,
                borderColor: '#d7d1ca',
                yaxis: { lines: { show: true } },
                padding: { top: -25, right: 12, bottom: 30, left: 12 },
            },
            legend: {
                show: true,
                position: 'bottom',
                horizontalAlign: 'center',
                markers: { shape: 'circle' },
            },
            stroke: {
                show: true,
                curve: 'straight',
                lineCap: 'round',
                width: 2,
            },
            tooltip: {
                enabled: true,
                shared: true,
                followCursor: true,
                marker: { show: true },
            },
            xaxis: {
                ...obj.options.xaxis,
                labels: {
                    rotate: -90,
                    rotateAlways: true,
                    hideOverlappingLabels: true,
                },
                crosshairs: {
                    show: true,
                    position: 'back',
                    stroke: { color: '#1c1e4d', width: 1, dashArray: 4 },
                },
                tooltip: { enabled: true },
            },
            yaxis: {
                labels: {
                    formatter: (value) => '$' + parseInt(value).toLocaleString(),
                },
                crosshairs: {
                    show: true,
                    stroke: { color: '#1c1e4d', width: 1, dashArray: 4 },
                },
                tooltip: { enabled: true },
            },
            responsive: [
                {
                    breakpoint: 1025,
                    options: {
                        chart: { height: '420px' },
                        grid: { padding: { top: -32, right: 8, bottom: 0, left: 0 } },
                        yaxis: {
                            labels: {
                                formatter: (value) => {
                                    return value >= 1000
                                        ? '$' + (value / 1000).toFixed(0) + 'K'
                                        : '$' + value.toFixed(0);
                                },
                                offsetX: -18,
                            },
                        },
                        xaxis: {
                            labels: { rotate: -45 },
                        },
                        legend: { horizontalAlign: 'left' },
                    },
                },
            ],
        };
    }

    function performanceBenchmarkChart(obj) {
        obj.options = {
            ...obj.options,
            chart: {
                type: 'bar',
                height: '640px',
                width: '100%',
                parentHeightOffset: 0,
                zoom: { enabled: false },
                toolbar: { show: false },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800,
                    animateGradually: { enabled: true, delay: 150 },
                    dynamicAnimation: { enabled: true, speed: 350 },
                },
                events: {
                    mounted: (chart) => {
                        setTimeout(() => chart.windowResizeHandler(), 500);
                    },
                },
            },
            dataLabels: {
                offsetY: -25,
                formatter: (value) => (value === null || value === 0 ? 'N/A' : value.toFixed(2)),
                style: { fontSize: '10px', colors: ['#1d235a'] },
            },
            grid: {
                show: true,
                borderColor: '#d7d1ca',
                yaxis: { lines: { show: true } },
                padding: { top: -25, right: -68, bottom: 50, left: 0 },
            },
            legend: {
                show: true,
                position: 'bottom',
                horizontalAlign: 'center',
                markers: { shape: 'circle' },
            },
            plotOptions: {
                bar: {
                    columnWidth: '92%',
                    dataLabels: { position: 'top', orientation: 'horizontal' },
                },
            },
            tooltip: {
                enabled: true,
                followCursor: true,
                y: {
                    formatter: (value) => (value === null || value === 0 ? null : value.toFixed(2) + '%'),
                },
            },
            xaxis: {
                categories: obj.options.xaxis.categories.map((label) =>
                    typeof label === 'string' ? label.replace(/<br\s*\/?>/gi, '\n').split('\n') : label,
                ),
                labels: { rotate: 0, offsetY: 12 },
                crosshairs: {
                    show: true,
                    position: 'back',
                    opacity: 0.1,
                    fill: { type: 'solid', color: '#4c60ac' },
                },
                tooltip: { enabled: false },
            },
            yaxis: {
                labels: {
                    offsetX: -8,
                    formatter: (value) => parseInt(value).toLocaleString() + '%',
                },
                crosshairs: {
                    show: true,
                    stroke: { color: '#1c1e4d', width: 1, dashArray: 4 },
                },
                tooltip: { enabled: true },
            },
            responsive: [
                {
                    breakpoint: 1279,
                    options: {
                        dataLabels: { offsetY: 12 },
                        grid: { padding: { top: 30, bottom: 30 } },
                        yaxis: {
                            labels: {
                                formatter: (value) => parseInt(value).toLocaleString() + '%',
                            },
                        },
                        plotOptions: {
                            bar: { dataLabels: { orientation: 'vertical' } },
                        },
                    },
                },
                {
                    breakpoint: 767,
                    options: {
                        chart: {
                            height: '860px',
                        },

                        dataLabels: {
                            formatter: (value) => (value === null || value === 0 ? 'N/A' : value.toFixed(2)) + '%',
                            textAnchor: 'start',
                            offsetY: 0,
                            offsetX: 3,
                        },
                        grid: {
                            padding: { top: -40, right: 0, bottom: 16, left: -10 },
                            row: {
                                colors: ['#f5f5f7', 'transparent'],
                                opacity: 0.45,
                            },
                            xaxis: { lines: { show: true } },
                            yaxis: { lines: { show: false } },
                        },
                        legend: {
                            horizontalAlign: 'left',
                            onItemClick: { toggleDataSeries: false },
                            onItemHover: { highlightDataSeries: false },
                        },
                        plotOptions: {
                            bar: {
                                barHeight: '80%',
                                horizontal: true,
                                dataLabels: {
                                    position: 'top',
                                    orientation: 'horizontal',
                                },
                            },
                        },
                        stroke: {
                            show: true,
                            width: 1,
                            colors: ['#fff'],
                        },
                        tooltip: {
                            shared: true,
                            intersect: false,
                            y: { formatter: (val) => Math.abs(val) + '%' },
                        },
                        xaxis: {
                            tickAmount: 6,
                            stepSize: 20,
                            labels: {
                                formatter: (value) => parseInt(value).toLocaleString() + '%',
                                offsetY: 0,
                            },
                        },
                        yaxis: {
                            position: 'bottom',
                            labels: {
                                align: 'center',
                                formatter: (value) => String(value).replace(/<br\s*\/?>|\n/g, ' '),
                                maxWidth: 132,
                                rotate: -90,
                            },
                            axisBorder: {
                                show: true,
                                color: '#78909C',
                            },
                        },
                        annotations: {
                            xaxis: [
                                {
                                    x: 0,
                                    strokeDashArray: 0,
                                    borderColor: '#e02826',
                                    label: {
                                        borderColor: '#e02826',
                                        style: {
                                            color: '#fff',
                                            background: '#e02826',
                                        },
                                    },
                                },
                            ],
                        },
                    },
                },
            ],
        };
    }

    // Select all chart elements
    const chartElements = document.querySelectorAll('.js-regional-chart');

    chartElements.forEach(function (chartElement) {
        // Extract data attributes
        const title = chartElement.dataset.title || '';
        const subtitle = chartElement.dataset.subtitle || '';
        const labels = chartElement.dataset.labels ? JSON.parse(chartElement.dataset.labels) : [];
        const seriesData = chartElement.dataset.series ? JSON.parse(chartElement.dataset.series).map(Number) : [];
        const colors = chartElement.dataset.colors ? JSON.parse(chartElement.dataset.colors) : [];

        // Configure chart options
        const options = {
            chart: {
                type: 'donut',
                height: 350,
                events: {
                    mounted: function (chartContext) {
                        // Adjust padding based on legend height
                        const legend = chartContext.el.querySelector('.apexcharts-legend');
                        if (legend) {
                            const legendHeight = legend.getBoundingClientRect().height;
                            chartElement.style.paddingBottom = `calc(${legendHeight}px + 1.25rem)`;
                        }
                    },
                },
            },
            series: seriesData,
            labels: labels,
            colors: colors,
            dataLabels: {
                enabled: false,
            },
            title: {
                text: title,
                align: 'center',
                floating: true,
                offsetY: 105,
                style: {
                    fontFamily: 'Noto Serif Light',
                    fontSize: '16px',
                    color: '#3a3632',
                },
            },
            subtitle: {
                text: subtitle,
                align: 'center',
                offsetY: 120,
                floating: true,
                style: {
                    fontSize: '64px',
                    fontFamily: 'Noto Serif Light',
                    color: '#1a204b',
                },
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '75%',
                    },
                },
            },
            legend: {
                formatter: function (seriesName, opts) {
                    const seriesIndex = opts.seriesIndex;
                    const seriesValue = opts.w.globals.series[seriesIndex];
                    return `<span class="legend-series-name">${seriesName}</span><span class="legend-data-label">${seriesValue}%</span>`;
                },
                position: 'bottom',
                floating: true,
                onItemClick: {
                    toggleDataSeries: true,
                },
                onItemHover: {
                    highlightDataSeries: true,
                },
            },
        };

        // Create and render the chart
        const chart = new ApexCharts(chartElement, options);
        chart.render();
    });
});
